// Flowing fluids (ENG-14; §5.5 phase 4; E-10). PURE + isomorphic: the algorithm
// runs over the narrow `FluidWorld` interface (World implements it) so the same
// code drives the client tick, the server, and tests. A fluid cell is scheduled
// for update when it or a neighbour changes; on update it recomputes its own
// level from its neighbours and then spreads. Water flows 7 blocks and forms
// infinite sources; lava flows 3 (4 in the nether). Lava meeting water turns to
// obsidian (a source) or cobblestone (flowing) — the classic interaction table.

export const FLUID_NONE = 0;
export const FLUID_WATER = 1;
export const FLUID_LAVA = 2;
export type FluidKind = 0 | 1 | 2;

/** Maximum flowing distance from a source, in blocks (water 7). */
export const MAX_LEVEL = 7;

/** The world surface the fluid algorithm reads and writes. */
export interface FluidWorld {
  getBlockState(x: number, y: number, z: number): number;
  /** FLUID_NONE / WATER / LAVA for a block state (waterlogged blocks are NONE —
   *  they hold water but do not themselves flow). */
  fluidKindOf(state: number): FluidKind;
  /** 0 (source) .. 7 (thinnest flow) for a fluid state; 0 for non-fluids. */
  fluidLevelOf(state: number): number;
  /** Can a fluid move into this cell? Air and washable plants (torch/cross), yes. */
  isReplaceableByFluid(state: number): boolean;
  /** Interned fluid state for (kind, level). */
  fluidStateOf(kind: FluidKind, level: number): number;
  readonly nether: boolean;
  readonly obsidianState: number;
  readonly cobblestoneState: number;
  /** Commit a block change (sets state + relight/remesh bookkeeping). */
  setFluidBlock(x: number, y: number, z: number, state: number): void;
  /** Queue a fluid update for the cell after that fluid's flow delay. */
  scheduleFluid(x: number, y: number, z: number): void;
}

const HORIZ: ReadonlyArray<readonly [number, number]> = [[1, 0], [-1, 0], [0, 1], [0, -1]];

/** Water spreads 1 level/block; lava 2 (overworld) or 1 (nether) → reach 3 / 7. */
function falloff(kind: FluidKind, nether: boolean): number {
  return kind === FLUID_LAVA && !nether ? 2 : 1;
}

/** Schedule fluid updates at a cell and its 6 neighbours (a change ripples out). */
export function scheduleAround(fw: FluidWorld, x: number, y: number, z: number): void {
  fw.scheduleFluid(x, y, z);
  fw.scheduleFluid(x, y + 1, z);
  fw.scheduleFluid(x, y - 1, z);
  for (const [dx, dz] of HORIZ) fw.scheduleFluid(x + dx, y, z + dz);
}

/** A cell is "falling" (rendered/treated as full, spreads like a source) when the
 *  same fluid sits directly above it. */
function isFalling(fw: FluidWorld, x: number, y: number, z: number, kind: FluidKind): boolean {
  return fw.fluidKindOf(fw.getBlockState(x, y + 1, z)) === kind;
}

/** How strongly a neighbour feeds this cell: sources and falling columns act as
 *  level 0 (full strength); other flow contributes its own level. */
function effectiveLevel(fw: FluidWorld, x: number, y: number, z: number, kind: FluidKind): number {
  const lvl = fw.fluidLevelOf(fw.getBlockState(x, y, z));
  if (lvl === 0) return 0;
  return isFalling(fw, x, y, z, kind) ? 0 : lvl;
}

/** Can fluid of `kind` move into (x,y,z)? Empty/washable, or shallower same fluid. */
function canFlowInto(fw: FluidWorld, x: number, y: number, z: number, kind: FluidKind, level: number): boolean {
  const s = fw.getBlockState(x, y, z);
  const k = fw.fluidKindOf(s);
  if (k === FLUID_NONE) return fw.isReplaceableByFluid(s);
  if (k === kind) return fw.fluidLevelOf(s) > level; // only downhill (thinner)
  return false; // different fluid — handled by the interaction check
}

/**
 * Horizontal flow direction of the fluid at (x,y,z), as a unit vector in the XZ
 * plane (or {0,0} for still/source-pooled/non-fluid cells). Flow points toward
 * thinner same-fluid neighbours and, more strongly, toward open cells the fluid
 * is spilling into — especially where it can then fall off an edge. Entities
 * standing in the fluid are pushed along this vector (E-10 item push). PURE.
 */
export function flowVector(fw: FluidWorld, x: number, y: number, z: number): { x: number; z: number } {
  const kind = fw.fluidKindOf(fw.getBlockState(x, y, z));
  if (kind === FLUID_NONE) return { x: 0, z: 0 };
  const own = effectiveLevel(fw, x, y, z, kind); // 0 = source/full, up to 7 = thin
  let fx = 0, fz = 0;
  for (const [dx, dz] of HORIZ) {
    const nx = x + dx, nz = z + dz;
    const ns = fw.getBlockState(nx, y, nz);
    const nk = fw.fluidKindOf(ns);
    let drop = 0;
    if (nk === kind) {
      drop = effectiveLevel(fw, nx, y, nz, kind) - own; // toward the thinner side
    } else if (fw.isReplaceableByFluid(ns)) {
      // Spilling into an empty cell; a strong pull toward an open edge it can fall off.
      const below = fw.getBlockState(nx, y - 1, nz);
      const canFall = fw.isReplaceableByFluid(below) || fw.fluidKindOf(below) === kind;
      drop = canFall ? MAX_LEVEL + 1 : MAX_LEVEL - own;
    }
    if (drop > 0) { fx += dx * drop; fz += dz * drop; }
  }
  const len = Math.hypot(fx, fz);
  return len === 0 ? { x: 0, z: 0 } : { x: fx / len, z: fz / len };
}

/** Any of the 6 neighbours is water (for the lava→obsidian/cobble reaction). */
function touchesWater(fw: FluidWorld, x: number, y: number, z: number): boolean {
  if (fw.fluidKindOf(fw.getBlockState(x, y + 1, z)) === FLUID_WATER) return true;
  if (fw.fluidKindOf(fw.getBlockState(x, y - 1, z)) === FLUID_WATER) return true;
  for (const [dx, dz] of HORIZ) if (fw.fluidKindOf(fw.getBlockState(x + dx, y, z + dz)) === FLUID_WATER) return true;
  return false;
}

/**
 * Run one fluid update at (x,y,z). Recomputes the cell's level from its
 * neighbours (draining if unsupported), applies the lava/water reaction, then
 * spreads down or outward. No-ops on non-fluid cells (inflow is driven by the
 * neighbouring fluid cells spreading in).
 */
export function tickFluid(fw: FluidWorld, x: number, y: number, z: number): void {
  const state = fw.getBlockState(x, y, z);
  const kind = fw.fluidKindOf(state);
  if (kind === FLUID_NONE) return;

  // Lava that touches water solidifies: source → obsidian, flow → cobblestone.
  if (kind === FLUID_LAVA && touchesWater(fw, x, y, z)) {
    const source = fw.fluidLevelOf(state) === 0;
    fw.setFluidBlock(x, y, z, source ? fw.obsidianState : fw.cobblestoneState);
    scheduleAround(fw, x, y, z);
    return;
  }

  const nether = fw.nether;
  const drop = falloff(kind, nether);
  let level = fw.fluidLevelOf(state);
  const source = level === 0;
  const falling = isFalling(fw, x, y, z, kind);

  // ── Recompute a flowing cell's level (sources persist). ──
  if (!source) {
    let newLevel: number;
    if (falling) {
      newLevel = 1; // fed from above → stays full (rendered full by the falling check)
    } else {
      let minEff = Number.POSITIVE_INFINITY;
      let sourceNeighbours = 0;
      for (const [dx, dz] of HORIZ) {
        if (fw.fluidKindOf(fw.getBlockState(x + dx, y, z + dz)) !== kind) continue;
        const e = effectiveLevel(fw, x + dx, y, z + dz, kind);
        if (e < minEff) minEff = e;
        if (fw.fluidLevelOf(fw.getBlockState(x + dx, y, z + dz)) === 0) sourceNeighbours++;
      }
      // Infinite water: a flow flanked by ≥2 horizontal sources becomes a source.
      if (kind === FLUID_WATER && sourceNeighbours >= 2) {
        newLevel = 0;
      } else if (minEff + drop <= MAX_LEVEL) {
        newLevel = minEff + drop;
      } else {
        // Unsupported → drain to air and let the neighbours re-settle.
        fw.setFluidBlock(x, y, z, fw.fluidStateOf(FLUID_NONE, 0));
        scheduleAround(fw, x, y, z);
        return;
      }
    }
    if (newLevel !== level) {
      fw.setFluidBlock(x, y, z, fw.fluidStateOf(kind, newLevel));
      scheduleAround(fw, x, y, z);
      level = newLevel;
    }
  }

  // ── Spread. Prefer flowing straight down; otherwise pool outward. ──
  const belowFalling = fw.getBlockState(x, y - 1, z);
  if (canFlowInto(fw, x, y - 1, z, kind, MAX_LEVEL) && fw.fluidKindOf(belowFalling) !== kind) {
    // Flow down as a falling column; don't also spread sideways from here.
    fw.setFluidBlock(x, y - 1, z, fw.fluidStateOf(kind, 1));
    scheduleAround(fw, x, y - 1, z);
    return;
  }

  // A FLOWING cell sitting on the same fluid is part of a column, not a pool surface,
  // and it does not fan out. Minecraft's `FlowingFluid.spread` spreads a non-source to
  // its sides only when the block below is not a "hole", and a block already holding
  // the same fluid IS a hole. Without this, every cell of a waterfall — water above
  // AND below — reached the spread below with `eff` 0 (it is "falling") and spread at
  // full strength, so a single source in open air fell as an ever-widening cone rather
  // than a column: ~200 cells on EVERY layer it passed through (D-TF-229). A source
  // still spreads regardless, and a falling cell that lands on something solid still
  // spreads at full strength, which is the bottom of a waterfall pooling out.
  if (!source && fw.fluidKindOf(belowFalling) === kind) return;

  const eff = source || isFalling(fw, x, y, z, kind) ? 0 : level;
  const spread = eff + drop;
  if (spread > MAX_LEVEL) return;
  for (const [dx, dz] of HORIZ) {
    const nx = x + dx, nz = z + dz;
    const ns = fw.getBlockState(nx, y, nz);
    const nk = fw.fluidKindOf(ns);
    if (nk !== FLUID_NONE && nk !== kind) {
      scheduleAround(fw, nx, y, nz); // adjacent other fluid — poke it to react
      continue;
    }
    if (canFlowInto(fw, nx, y, nz, kind, spread)) {
      fw.setFluidBlock(nx, y, nz, fw.fluidStateOf(kind, spread));
      scheduleAround(fw, nx, y, nz);
    }
  }
}
